import { useApp, useBehavior, useList, useTriggerAction } from '@libs/core';
import { TWidgetProps } from '@libs/types';
import React from 'react';
import { TConsumerInputPhoneData, TConsumerItem, TConsumerListData } from '../../../types/data';
import { RowEnum } from '../../../types/enums';
import { TListOption } from '../../../types/options';
import { ListSelectView } from './ListSelectView';

type TListSelectWidgetProps = {
    rowId?: RowEnum.SELECTOR_REGULAR | RowEnum.ROW_REGULAR;
} & TWidgetProps<TConsumerListData & TConsumerInputPhoneData, TListOption>;

const convertItemsToListSelect = (items?: Array<TConsumerItem>, currentValue?: string, isDisabled?: boolean) =>
    Array.isArray(items) ? items?.map?.((item) => convertItemToListSelect(item, currentValue, isDisabled) as TConsumerItem) : [];

const convertItemToListSelect = (item: TConsumerItem, value?: string, isDisabled?: boolean): TConsumerItem => {
    const normalizedValue = value?.toLowerCase?.();
    return {
        ...item,
        isDisabled: isDisabled ? true : item.isDisabled,
        selected: item?.selected
            ? true
            : !!normalizedValue && (item.id?.toLowerCase?.() === normalizedValue || (item.value as string)?.toLowerCase?.() === normalizedValue),
    };
};

/**
 * Standalone list-select widget driven entirely by server-provided configuration.
 * Owns behavior hooks (useBehavior, useList, useTriggerAction, useApp) and
 * delegates all rendering to ListSelectView.
 *
 * Use this component when the widget definition comes from the server and the
 * full behavior lifecycle (modal management, item selection, search) is required.
 */
export const ListSelectWidget: React.FC<TListSelectWidgetProps> = ({ widget, rowId }) => {
    const { isHidden, isDisabled } = useBehavior(widget);
    const { setModal, setPopup, setBottomSheet } = useApp();
    const { triggerActionsByType } = useTriggerAction(widget);
    const { handleClick } = useList(widget, { triggerActionsByType, setModal, setPopup, setBottomSheet });

    const dataForListSelect = {
        ...widget?.data,
        items: convertItemsToListSelect(widget?.data?.items, widget.data.value as string, isDisabled),
    };

    return (
        <ListSelectView
            widget={widget}
            isHidden={isHidden}
            isDisabled={isDisabled}
            dataForListSelect={dataForListSelect}
            onSelectItem={handleClick}
            rowId={rowId as RowEnum}
        />
    );
};
